import type { MetaDescriptor } from "react-router";

import { env } from "@/lib/env";
import publishedManifest from "../../../public/locales/manifest.json";

// Materialized at build time: do not advertise unpublished languages.
export const publishedLocaleAlternates = publishedManifest.locales.map(({ code }) => ({ code }));
import {
    breadcrumbSchema,
    organizationSchema,
    webSiteSchema,
    type BreadcrumbItem,
} from "./structuredData";

/**
 * Input shape for {@link buildSeoMeta}.
 *
 * Pure-function helper used inside route `meta` exports. Takes locale +
 * availableLocales as inputs (route `meta` reads them from the root loader
 * manifest as of Phase 6) so it can build absolute canonical / hreflang URLs
 * without any React-only context.
 */
/** Brand suffix appended to every page title by default. */
export const DEFAULT_TITLE_SUFFIX = " | Stork Rentals";

export interface BuildSeoMetaInput {
    /**
     * Page title — without the brand suffix.
     *
     * `buildSeoMeta` automatically appends `titleSuffix` (default
     * `" | Stork Rentals"`) so callers don't need to repeat the brand. Pass
     * `titleSuffix: ""` to suppress the suffix for one-off cases (e.g. the
     * root default title which already includes the brand).
     */
    title: string;
    description?: string;
    /**
     * Locale-relative path, e.g. "/catalogue" or "/product/my-slug". Optional;
     * when omitted, the canonical URL points at the locale root.
     */
    path?: string;
    /** OG/Twitter image — absolute URL or site-relative path. */
    image?: string;
    imageAlt?: string;
    type?: "website" | "article" | "product";
    /** Exclude this page from search indexes (private/auth pages). */
    noindex?: boolean;
    keywords?: string;
    /** JSON-LD structured data object(s) to embed. */
    jsonLd?: Record<string, unknown> | Record<string, unknown>[];
    /** Breadcrumb trail — rendered as a BreadcrumbList JSON-LD node. */
    breadcrumbs?: BreadcrumbItem[];
    /** Active locale code (e.g. "en", "es"). */
    locale: string;
    /** All locales the site is published in (used for hreflang alternates). */
    availableLocales: { code: string }[];
    /**
     * Suffix appended to `title` (default: `" | Stork Rentals"`).
     *
     * Pass `""` to suppress the suffix, or a custom string for one-off cases.
     * If `title` already ends with the brand the suffix is skipped to avoid
     * `"… | Stork Rentals | Stork Rentals"`.
     */
    titleSuffix?: string;
}

function toAbsoluteUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const base = (env.CUSTOMER_MULTIDOMAIN
        ? (pathOrUrl.startsWith("/es/") ? env.ES_SITE_URL : env.EN_SITE_URL)
        : env.SITE_URL).replace(/\/+$/, "");
    return `${base}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

/** Ensure a path ends with exactly one trailing slash (root-safe). */
function withTrailingSlash(p: string): string {
    if (!p) return "/";
    return p.endsWith("/") ? p : `${p}/`;
}

/**
 * Locale-relative absolute path with a guaranteed trailing slash.
 *
 * The site is served from an S3 static-website origin whose prerendered objects
 * are directory-style (`/en/faq/index.html`), so the canonical, indexable URL
 * for every page carries a trailing slash (`/en/faq/`; locale root `/en/`).
 * Emitting the slash here keeps canonical, hreflang, x-default and og:url in
 * lockstep with what the origin serves with a 200 — otherwise each URL is a
 * 302 redirector whose target's canonical points back at it, which Google reads
 * as redirect churn and declines to index.
 */
function localeAbsPath(locale: string, path?: string): string {
    if (!path || path === "/") return `/${locale}/`;
    const normalized = path.startsWith("/") ? path : `/${path}`;
    return withTrailingSlash(`/${locale}${normalized}`);
}

function buildLdJsonDescriptors(jsonLd: BuildSeoMetaInput["jsonLd"]): MetaDescriptor[] {
    if (!jsonLd) return [];
    const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    return items.map((schema) => ({ "script:ld+json": schema }));
}

function buildBreadcrumbDescriptors(
    breadcrumbs: BreadcrumbItem[] | undefined,
    locale: string,
): MetaDescriptor[] {
    if (!breadcrumbs?.length) return [];
    return [{ "script:ld+json": breadcrumbSchema(breadcrumbs, locale) }];
}

/**
 * Build the array of `MetaDescriptor`s for a route's `meta` export.
 *
 * Emits: title, description, optional keywords, canonical link (locale-aware
 * absolute), hreflang alternates for every available locale + x-default,
 * OpenGraph (type, title, description, url, image, alt), Twitter card, optional
 * robots noindex, and arbitrary JSON-LD descriptors (including a breadcrumb
 * `BreadcrumbList` node when `breadcrumbs` is provided).
 *
 * All URLs are absolute and built from `env.SITE_URL` so the markup remains
 * correct under prerender (where `window.location.origin` would be the local
 * build server).
 */
export function buildSeoMeta(input: BuildSeoMetaInput): MetaDescriptor[] {
    const localePath = localeAbsPath(input.locale, input.path);
    const xDefaultPath =
        input.path && input.path !== "/"
            ? withTrailingSlash(input.path.startsWith("/") ? input.path : `/${input.path}`)
            : "/";

    const suffix = input.titleSuffix ?? DEFAULT_TITLE_SUFFIX;
    const fullTitle =
        !suffix || input.title.endsWith(suffix) || input.title.endsWith("Stork Rentals")
            ? input.title
            : `${input.title}${suffix}`;

    const descriptors: MetaDescriptor[] = [
        { title: fullTitle },
        ...(input.description ? [{ name: "description", content: input.description }] : []),
        ...(input.keywords ? [{ name: "keywords", content: input.keywords }] : []),
        { tagName: "link", rel: "canonical", href: toAbsoluteUrl(localePath) },
        ...input.availableLocales.map((l) => ({
            tagName: "link" as const,
            rel: "alternate" as const,
            hrefLang: l.code,
            href: toAbsoluteUrl(localeAbsPath(l.code, input.path)),
        })),
        { tagName: "link", rel: "alternate", hrefLang: "x-default", href: toAbsoluteUrl(xDefaultPath) },
        { property: "og:type", content: input.type ?? "website" },
        { property: "og:title", content: fullTitle },
        ...(input.description ? [{ property: "og:description", content: input.description }] : []),
        { property: "og:url", content: toAbsoluteUrl(localePath) },
        ...(input.image ? [{ property: "og:image", content: toAbsoluteUrl(input.image) }] : []),
        ...(input.imageAlt ? [{ property: "og:image:alt", content: input.imageAlt }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: fullTitle },
        ...(input.description ? [{ name: "twitter:description", content: input.description }] : []),
        ...(input.image ? [{ name: "twitter:image", content: toAbsoluteUrl(input.image) }] : []),
        ...(input.noindex ? [{ name: "robots", content: "noindex,nofollow" }] : []),
        // Sitewide JSON-LD (Organization + WebSite). Emitted from every route's
        // meta because RR v7 child meta REPLACES parent meta — the root meta's
        // descriptors would otherwise be dropped on every route that exports
        // its own `meta`. Mirroring the schema on every page is what helmet's
        // `<SiteStructuredData>` did before Phase 7A, just without the helmet.
        { "script:ld+json": organizationSchema() },
        { "script:ld+json": webSiteSchema(input.locale) },
        ...buildLdJsonDescriptors(input.jsonLd),
        ...buildBreadcrumbDescriptors(input.breadcrumbs, input.locale),
    ];

    return descriptors;
}
